import { useEffect, useState } from 'react'

interface InfoAccount {
  account: string
  password: string
}

interface PasswordCredentialLike {
  id: string
  password: string
}

interface PasswordCredentialConstructor {
  new (data: { id: string; password: string }): unknown
}

function getCredentialsContainer(): CredentialsContainer | null {
  if (typeof window === 'undefined' || !('credentials' in navigator) || !navigator.credentials) {
    return null
  }
  return navigator.credentials
}

function isPasswordCredential(value: unknown): value is PasswordCredentialLike {
  if (!value || typeof value !== 'object') {
    return false
  }
  const credential = value as Partial<PasswordCredentialLike>
  return typeof credential.id === 'string' && typeof credential.password === 'string'
}

function getPasswordCredentialConstructor(): PasswordCredentialConstructor | null {
  if (typeof window === 'undefined') {
    return null
  }
  const ctor = (window as Window & { PasswordCredential?: PasswordCredentialConstructor })
    .PasswordCredential
  return ctor ?? null
}

export async function saveRememberMeCredential(account: string, password: string) {
  const credentials = getCredentialsContainer()
  const PasswordCredentialCtor = getPasswordCredentialConstructor()
  if (!credentials || !PasswordCredentialCtor) {
    return
  }
  try {
    const credential = new PasswordCredentialCtor({ id: account, password })
    await credentials.store(credential as Credential)
  } catch {
    console.error('Failed to save remember me credential to browser-level storage')
  }
}

export async function clearRememberMeCredential() {
  const credentials = getCredentialsContainer()
  if (!credentials?.preventSilentAccess) {
    return
  }
  try {
    await credentials.preventSilentAccess()
  } catch {
    console.error('Failed to clear remember me credential from browser-level storage')
  }
}

export function useRememberMe() {
  const [infoAccount, setInfoAccount] = useState<InfoAccount | null>(null)

  useEffect(() => {
    const credentialsContainer = getCredentialsContainer()
    if (!credentialsContainer) {
      return
    }
    const credentialsApi: CredentialsContainer = credentialsContainer

    async function getRememberedCredential() {
      try {
        const credential = await credentialsApi.get({
          mediation: 'optional',
          password: true,
        } as CredentialRequestOptions)
        if (!isPasswordCredential(credential)) {
          return
        }

        setInfoAccount({
          account: credential.id,
          password: credential.password,
        })
      } catch {
        console.error('Failed to get remember me credential from browser-level storage')
      }
    }

    void getRememberedCredential()
  }, [])

  return { infoAccount }
}
